import { computed, inject, Injectable } from '@angular/core';
import { SettingsStore } from '@settings/data-access';
import * as currency from 'locale-currency';
import { DateTime } from 'luxon';
import { getDateTime, isFormattableInstant } from '@shared/utils';

@Injectable({ providedIn: 'root' })
export class FormattingService {
  private readonly defaultCulture = 'pt-BR';
  private readonly settingsStore = inject(SettingsStore);
  private readonly ownerSettings = computed(() =>
    this.settingsStore.ownerSettings()
  );
  private readonly culture = computed(
    () => this.ownerSettings()?.Culture ?? this.defaultCulture
  );

  public normalizeNumber(value: string | number): number {
    if (!value && value !== 0) return 0;

    const numericValue =
      typeof value === 'string'
        ? parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.'))
        : value;

    if (isNaN(numericValue)) return 0;

    return numericValue;
  }

  /**
   * @param options extra `Intl.NumberFormat` options merged over the defaults — e.g.
   * `{ notation: 'compact' }` to abbreviate values that would overflow their container.
   */
  public formatCurrency(
    value: number | string,
    locale?: string,
    options?: Intl.NumberFormatOptions
  ) {
    const numericValue = this.normalizeNumber(value);
    const cultureToUse = locale ?? this.culture();
    const currencyCode = currency.getCurrency(cultureToUse) ?? 'XXX';

    return new Intl.NumberFormat(cultureToUse, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol',
      ...options,
    }).format(numericValue);
  }

  public formatToPartsCurrency(value: number | string, locale?: string) {
    const numericValue = this.normalizeNumber(value);
    const cultureToUse = locale ?? this.culture();
    const currencyCode = currency.getCurrency(cultureToUse) ?? 'XXX';

    return new Intl.NumberFormat(cultureToUse, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol',
    }).formatToParts(numericValue);
  }

  /**
   * Splits a formatted currency string into symbol and numeric part (owner culture / currency).
   */
  public splitCurrencyParts(
    value: number | string | null | undefined,
    locale?: string
  ): { symbol: string; amount: string } {
    const cultureToUse = locale ?? this.culture();
    const currencyCode = currency.getCurrency(cultureToUse) ?? 'XXX';
    const formatter = new Intl.NumberFormat(cultureToUse, {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'symbol',
    });
    const currencySymbolFromParts = (): string =>
      formatter
        .formatToParts(0)
        .find((p) => p.type === 'currency')
        ?.value?.trim() ?? '';

    if (value === null || value === undefined) {
      return { symbol: currencySymbolFromParts(), amount: '—' };
    }
    if (typeof value === 'string') {
      const t = value.trim();
      if (t === '' || t === '—') {
        return { symbol: currencySymbolFromParts(), amount: '—' };
      }
    }
    const numericValue =
      typeof value === 'number' ? value : this.normalizeNumber(value);
    if (!Number.isFinite(numericValue)) {
      return { symbol: currencySymbolFromParts(), amount: '—' };
    }
    const parts = formatter.formatToParts(numericValue);
    let symbol = '';
    let amount = '';
    for (const p of parts) {
      if (p.type === 'currency') {
        symbol += p.value;
      } else {
        amount += p.value;
      }
    }
    return { symbol: symbol.trim(), amount: amount.trim() };
  }

  /**
   * Currency with explicit +/- prefix (non-zero), matching treasury signed summaries.
   */
  public formatSignedCurrency(
    value: number | string | null | undefined,
    locale?: string
  ): string {
    if (value === null || value === undefined) {
      return '—';
    }
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) {
        return '—';
      }
      const n = value;
      if (Math.abs(n) < 0.01) {
        return this.formatCurrency(0, locale).replace(/\u00a0/g, ' ');
      }
      const sign = n > 0 ? '+' : '-';
      return `${sign}${this.formatCurrency(Math.abs(n), locale).replace(
        /\u00a0/g,
        ' '
      )}`;
    }
    const trimmed = String(value).trim();
    if (trimmed === '' || trimmed === '—') {
      return '—';
    }
    const parsed = Number(trimmed.replace(',', '.'));
    if (!Number.isFinite(parsed)) {
      return '—';
    }
    return this.formatSignedCurrency(parsed, locale);
  }

  /**
   * Formata uma data no fuso e na cultura da conta.
   *
   * O valor passa pelo `getDateTime`, ou seja chega aqui **já em hora de parede
   * da conta** — a conversão de UTC mora na fronteira com o backend, não aqui.
   * Ler o JSDoc do `getDateTimeFromJSDate` antes de assumir o que um `Date`
   * vindo de uma grelha representa: não é um instante UTC.
   *
   * ## Sobre `options.timeZone`
   *
   * É honrado. O Luxon passa-o intacto ao `Intl` — o `PolyDateFormatter` tem um
   * ramo explícito para isso (*"Don't apply any workarounds if a timeZone is
   * explicitly provided in opts"*, `luxon/src/impl/locale.js`). Verificado:
   * `timeZone: 'UTC'` sobre 01/04 00:00 em BRT dá 03:00, igual ao `Intl` cru.
   *
   * Mas **quase nunca é o que se quer**. O valor já está no fuso da conta;
   * passar `timeZone` desloca-o outra vez. Foi por isso que os `timeZone: 'UTC'`
   * espalhados pelos templates foram removidos — eram compensação para um bug
   * que já não existe, e reintroduzi-los faz o valor render um dia antes.
   *
   * Para data+hora, passar `{ hour: '2-digit', minute: '2-digit' }`.
   */
  public formatDate(
    date: string | Date | number | DateTime,
    options?: Intl.DateTimeFormatOptions,
    locale?: string,
  ) {
    const cultureToUse = locale ?? this.culture();
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };

    const dt = getDateTime(date);
    if (!dt?.isValid) return '';
    const mergedOptions = { ...defaultOptions, ...options };
    const result = dt.toLocaleString(mergedOptions, { locale: cultureToUse });
    return result;
  }

  /**
   * Formats a time preserving the timezone from the input string when it's an ISO string.
   * For plain time strings (HH:mm or HH:mm:ss), uses the owner's configured timezone.
   */
  public formatTime(
    time: Date | string | number | DateTime,
    options?: Intl.DateTimeFormatOptions,
    locale?: string
  ) {
    const cultureToUse = locale ?? this.culture();
    const defaultOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
    };
    const mergedOptions = { ...defaultOptions, ...options };

    if (typeof time === 'string') {
      const trimmedTime = time.trim();
      if (!trimmedTime) return '';

      const timePattern =
        /(?:^|\D)([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?(?!\d)/;
      const match = trimmedTime.match(timePattern);

      if (match) {
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
          const dt = DateTime.now()
            .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
          return dt.toLocaleString(mergedOptions, { locale: cultureToUse });
        }
        return '';
      }
    }

    const dt = getDateTime(time);
    if (!dt?.isValid) return '';
    return dt.toLocaleString(mergedOptions, { locale: cultureToUse });
  }

  public formatDateTime(
    dateTime: string | Date | number | DateTime,
    options?: Intl.DateTimeFormatOptions
  ) {
    const dateTimeObj: DateTime | null = getDateTime(dateTime)
    if (!dateTimeObj?.isValid) return '';

    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    };

    return dateTimeObj.toLocaleString(options ?? defaultOptions, {
      locale: this.culture(),
    });
  }

  public formatNumber(
    value: number | string,
    decimals?: number,
    locale?: string,
    options?: Intl.NumberFormatOptions
  ) {
    const numericValue = this.normalizeNumber(value);
    const cultureToUse = locale ?? this.culture();

    return new Intl.NumberFormat(
      cultureToUse,
      options || {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }
    ).format(numericValue);
  }

  public getCurrencySymbol(): string {
    const currencyCode = currency.getCurrency(this.culture());
    const formatter = new Intl.NumberFormat(this.culture(), {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const parts = formatter.formatToParts(0);
    const currencySymbol =
      parts.find((part) => part.type === 'currency')?.value ?? '';
    return currencySymbol;
  }

  public getSeparators(): {
    thousandSeparator: string;
    decimalSeparator: string;
  } {
    const numberFormatter = new Intl.NumberFormat(this.culture());
    const parts = numberFormatter.formatToParts(1234567.89);
    const thousandSeparator =
      parts.find((part) => part.type === 'group')?.value ?? ',';
    const decimalSeparator =
      parts.find((part) => part.type === 'decimal')?.value ?? '.';
    return { thousandSeparator, decimalSeparator };
  }

  /**
   * Instante de referência para as máscaras e os padrões de data/hora.
   *
   * Estes quatro métodos só leem a ORDEM dos campos e os separadores que o
   * `Intl` devolve para a cultura da conta — o valor da data é irrelevante,
   * porque dia, mês, ano, hora e minuto são todos substituídos por `00`/`0000`
   * ou por `dd`/`MM`/`yyyy`. O que NÃO é irrelevante é o instante ser
   * formatável: `formatToParts` lança `RangeError: Invalid time value` para
   * `NaN`, e `DateTime.now().toMillis()` devolve `NaN` sempre que o `Settings.now`
   * da app (ligado ao relógio sincronizado) ou o `Settings.defaultZone` estiverem
   * comprometidos. Era isso o Sentry THE-NEW-130: um relógio de estação partido
   * a rebentar a máscara de TODOS os inputs de data da aplicação.
   *
   * O `ServerClockService` já garante a invariante do lado dele; esta guarda
   * existe porque o custo de a ter é uma comparação e o custo de não a ter é uma
   * exceção em pleno `change detection`, num sítio que nunca precisou da hora.
   */
  private maskReferenceInstant(): number {
    const now = DateTime.now().toMillis();
    return isFormattableInstant(now) ? now : 0;
  }

  public getDateMask(): string {
    const formatter = new Intl.DateTimeFormat(this.culture(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = formatter.formatToParts(this.maskReferenceInstant());

    return (
      parts
        .map((part) => {
          if (part.type === 'day' || part.type === 'month') return '00';
          if (part.type === 'year') return '0000';
          return part.value;
        })
        .join('') || '00/00/0000'
    );
  }

  public getDateFormatPattern(): string {
    const formatter = new Intl.DateTimeFormat(this.culture(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = formatter.formatToParts(this.maskReferenceInstant());

    return (
      parts
        .map((part) => {
          if (part.type === 'day') return 'dd';
          if (part.type === 'month') return 'MM';
          if (part.type === 'year') return 'yyyy';
          return part.value;
        })
        .join('') || 'dd/MM/yyyy'
    );
  }

  public getTimeMask(): string {
    // `hour12: false` para casar com o `getTimeFormatPattern`, que já o declara.
    // Sem isto o `Intl` acrescenta o `dayPeriod` numa cultura de 12 horas e a
    // máscara sai `00:00 AM`/`00:00 PM` — enquanto o padrão com que o valor é
    // depois lido (`updateDate`) é `HH:mm`. Os dois são usados aos pares, e
    // discordavam. Em `pt-BR` não muda nada.
    const formatter = new Intl.DateTimeFormat(this.culture(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = formatter.formatToParts(this.maskReferenceInstant());

    return (
      parts
        .map((part) => {
          if (part.type === 'hour' || part.type === 'minute') return '00';
          return part.value;
        })
        .join('') || '00:00'
    );
  }

  public getTimeFormatPattern(): string {
    const formatter = new Intl.DateTimeFormat(this.culture(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = formatter.formatToParts(this.maskReferenceInstant());

    return (
      parts
        .map((part) => {
          if (part.type === 'hour') return 'HH';
          if (part.type === 'minute') return 'mm';
          return part.value;
        })
        .join('') || 'HH:mm'
    );
  }

  public getDateTimeMask(): string {
    return this.getDateMask() + ' ' + this.getTimeMask();
  }

  public getDateTimeFormatPattern(): string {
    return this.getDateFormatPattern() + ' ' + this.getTimeFormatPattern();
  }
}
